import type { DashboardTemplate } from '@/entities/dashboard-template';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useAppForm } from '@/shared/ui/form';

import { useCreateDashboardFromTemplate } from '../model/use-create-dashboard-from-template';
import { CreationProgressOverlay } from './creation-progress-overlay';

type TemplateFormDialogProps = {
  template: DashboardTemplate;
  // withForm으로 생성된 FormBody 컴포넌트.
  // TanStack Form의 invariant 제네릭으로 인해 form prop 타입을 정적으로 표현 불가.
  // 각 템플릿의 FormBody + formConfig 쌍 관리로 런타임 안전성 보장.
  FormBody: React.ComponentType<{ form: ReturnType<typeof useAppForm> }>;
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
};

export const TemplateFormDialog = ({
  template,
  FormBody,
  isOpen,
  onClose,
  onExitComplete,
}: TemplateFormDialogProps) => {
  const { create, steps, isCreating } = useCreateDashboardFromTemplate();

  const form = useAppForm({
    defaultValues: {
      ...template.formConfig.defaultValues,
      title: template.name,
    },
    validators: {
      onChange: template.formConfig.schema,
    },
    onSubmit: async ({ value }) => {
      await create(template, value);
      onClose();
    },
  });

  return (
    <Dialog open={isOpen} onExitComplete={onExitComplete}>
      <DialogContent size="s" className="overflow-hidden p-0">
        <div className="relative flex h-full flex-col overflow-hidden">
          <CreationProgressOverlay steps={steps} isCreating={isCreating} />

          <form.Root
            className="flex h-full flex-col overflow-hidden"
            onSubmit={form.handleSubmit}
          >
            <DialogHeader className="shrink-0">
              <DialogTitle>Create dashboard from template</DialogTitle>
              <DialogDescription>{template.description}</DialogDescription>
            </DialogHeader>

            <DialogBody className="flex-1 overflow-y-auto py-4">
              <div className="flex flex-col gap-6">
                {/* @ts-expect-error */}
                <FormBody form={form} />
              </div>
            </DialogBody>

            <DialogFooter className="shrink-0 border-t bg-white">
              <div />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isCreating}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <form.Subscribe selector={(state) => state.canSubmit}>
                  {(canSubmit) => (
                    <Button
                      type="submit"
                      disabled={!canSubmit || isCreating}
                      isLoading={isCreating}
                    >
                      Create dashboard
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </DialogFooter>
          </form.Root>
        </div>
      </DialogContent>
    </Dialog>
  );
};
